// appformcomp.js
import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormHelperText,
  Link,
  Alert,
} from '@mui/material';
import { Grid } from '@mui/material';
import ScheduleInput from '../ScheduleInput'; // Import ScheduleInput
import { getDocumentUrl } from '../../utils/authUtils';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { useOrg } from '../../context/OrgContext';

const countries = [
  'United States',
  'Canada',
  // Add other countries as needed
];

const statesProvinces = {
  'United States': [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI',
    'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI',
    'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
    'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
    'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  ],
  Canada: [
    'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'PE', 'QC', 'SK', 'NT', 'NU', 'YT',
  ],
};

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const yearsOptions = [
  'Less than a year',
  '1-5 years',
  '5-10 years',
  'More than 10 years',
];

const dispatchSoftwareOptions = [
  'TRAXERO Dispatch Anywhere',
  'Towbook',
  'Bosch Towing Management',
  'OctopusPro',
  'Roadside Protect Navigator',
  'Agero Swoop',
  'TowSoft',
  'Omadi Core',
  'InTow Software',
  'TOPS (Beacon)',
  'VTS Cloud',
  'Ranger SST',
  'Other',
  // Add more as needed
];

const ApplicationForm = () => {
  const { name: orgName, supportEmail, supportPhone, isPlatform } = useOrg();
  // White Knight's agreement and code of conduct are static PDFs; a partner's are its published documents
  const agreementUrl = isPlatform ? '/terms-and-conditions.pdf' : `${process.env.REACT_APP_API_URL || ''}/api/public/org/documents/providerAgreement`;
  const conductUrl = isPlatform ? '/code-of-conduct.pdf' : `${process.env.REACT_APP_API_URL || ''}/api/public/org/documents/codeOfConduct`;
  // Temporary providers invited to sign up arrive with ?vendorId=... — the
  // submission carries their existing Vendor ID so approval upgrades their
  // temporary facility/login in place (job history included).
  const linkedVendorId = React.useMemo(
    () => new URLSearchParams(window.location.search).get('vendorId') || '',
    []
  );

  const [formData, setFormData] = useState({
    companyName: '',
    mcNumber: '',
    dotNumber: '',
    ownerFirstName: '',
    ownerLastName: '',
    email: '',
    phoneCountry: 'United States',
    phoneCountryCode: '+1',
    phoneNumber: '',
    facilityCountry: 'United States',
    facilityAddress1: '',
    facilityAddress2: '',
    facilityCity: '',
    facilityState: '',
    facilityZip: '',
    billingSame: 'yes',
    billingAddress: {
      country: 'United States',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
    services: {
      // Roadside Services
      jumpStartLight: false,
      jumpStartMedium: false,
      jumpStartHeavy: false,
      tireChangeLight: false,
      tireChangeMedium: false,
      tireChangeHeavy: false,
      lockOutLight: false,
      lockOutMedium: false,
      lockOutHeavy: false,
      fuelDeliveryLight: false,
      fuelDeliveryMedium: false,
      fuelDeliveryHeavy: false,
      winchOutLight: false,
      winchOutMedium: false,
      winchOutHeavy: false,
      
      // Towing Services
      lightDutyTowing: false,
      mediumDutyTowing: false,
      heavyDutyTowing: false,
      accidentSceneTowing: false,
      secondaryTow: false,
      
      // Other Services
      mobileMechanic: false,
      mediumHeavyTire: false,
      storageFacility: false,
      keyMaking: false,
      locksmith: false,
      electricCharging: false,
      
      // Schedule
      schedule: { // Add initial schedule state
        open247: false, // Default to false so applicants actively provide their hours
        sameEveryDay: false,
        sameTimeSelectedDays: false,
        everyDayOpen: '',
        everyDayClose: '',
        days: {
          monday: { isOpen: false, open: '', close: '' },
          tuesday: { isOpen: false, open: '', close: '' },
          wednesday: { isOpen: false, open: '', close: '' },
          thursday: { isOpen: false, open: '', close: '' },
          friday: { isOpen: false, open: '', close: '' },
          saturday: { isOpen: false, open: '', close: '' },
          sunday: { isOpen: false, open: '', close: '' }
        }
      }
    },
    territories: {
      zipCodeFile: null,
      zipCodes: '',
    },
    w9: null,
    w9Address: '',
    backupWithholding: false,
    signature: '',
    employees: '',
    backgroundCheck: null,
    insurance: {
      coi: null,
      agency: '',
      policyNumber: '',
      agentName: '',
      agentPhone: '',
      agentFax: '',
      agentEmail: '',
      policyExpiration: '',
    },
    businessInfo: {
      yearsInBusiness: '',
      electricVehicleExp: '',
      digitalDispatch: '',
      dispatchSoftware: '', // Added field for dispatch software
      otherMotorclubServices: '', // Added field for other motor clubs
      otherMotorclubsList: '', // Added field for listing other motor clubs
    },
    ownership: {
      familyOwned: false,
      womenOwned: false,
      minorityOwned: false,
      veteranOwned: false,
      lgbtqOwned: false,
      smallBusiness: false,
      disadvantagedBusiness: false,
    },
    referralCode: '',
    leadSource: '',
    termsAgreement: false,
    codeOfConductAgreement: false,
  });

  // Submission lifecycle: idle → submitting → success | error
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');

  // Handler for form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitStatus === 'submitting' || submitStatus === 'success') return; // no double-submits

    // Create a copy of formData with properly formatted services
    const submissionData = new FormData();
    
    // Transform services data to ensure boolean values
    const transformedServices = {};
    Object.keys(formData.services).forEach(key => {
      if (key === 'schedule') {
        // The form tracks open247 inside schedule, but the Application schema stores it
        // at services.open247 (schedule has no open247 field, so it would be silently
        // dropped on save). Split it out so the applicant's choice actually persists.
        const { open247, ...scheduleRest } = formData.services.schedule || {};
        transformedServices.schedule = scheduleRest;
        transformedServices.open247 = Boolean(open247);
      } else {
        // Transform other service keys (checkboxes) to booleans
        transformedServices[key] = Boolean(formData.services[key]);
      }
    });
    
    // Create the final data object with transformed services
    const finalData = {
      ...formData,
      services: transformedServices,
      // Attach the invited temporary provider's Vendor ID (validated server-side)
      ...(linkedVendorId && { vendorId: linkedVendorId })
    };
  
    // Append the transformed data
    submissionData.append('data', JSON.stringify(finalData));
    
    // Append files if they exist
    if (formData.w9) submissionData.append('w9', formData.w9);
    if (formData.backgroundCheck) submissionData.append('backgroundCheck', formData.backgroundCheck);
    if (formData.insurance?.coi) submissionData.append('coi', formData.insurance.coi);
    if (formData.territories?.zipCodeFile) submissionData.append('zipCodeFile', formData.territories.zipCodeFile);
  
    // Submit to server — with visible feedback so applicants know it worked
    // (and don't click Submit six times)
    setSubmitStatus('submitting');
    setSubmitError('');
    fetch(`${process.env.REACT_APP_API_URL}/api/applications`, {
      method: 'POST',
      body: submissionData,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'Failed to submit application. Please try again.');
        }
        return data;
      })
      .then(() => {
        setSubmitStatus('success');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((error) => {
        setSubmitStatus('error');
        setSubmitError(error.message || 'Failed to submit application. Please try again.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };


  // Handler for top-level fields
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle phone country selection
    if (name === 'phoneCountry') {
      const countryCode = value === 'United States' ? '+1' : '+1'; // Adjust country codes as needed
      setFormData((prev) => ({
        ...prev,
        phoneCountry: value,
        phoneCountryCode: countryCode,
      }));
    } else if (name.startsWith('billingAddress.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          [field]: value,
        },
      }));
    } else {
      // Phone fields accept any format and normalize to (XXX) XXX-XXXX
      const nextValue = name === 'phoneNumber' ? formatPhoneNumber(value) : value;
      setFormData((prev) => ({
        ...prev,
        [name]: nextValue,
      }));
    }
  };

  // Handler for nested fields (services - excluding schedule)
  const handleServicesChange = (e) => {
    const { name, checked } = e.target; // Only checkboxes left here directly under services
    setFormData((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [name]: checked,
      },
    }));
  };

  // Handler for schedule changes within services
  const handleServiceScheduleChange = (fieldPath, value) => {
    console.log(`Updating ${fieldPath} to ${value}`);
    
    // Handle the special case for open247 which is at the top level
    if (fieldPath === 'open247') {
      setFormData(prev => ({
        ...prev,
        services: {
          ...prev.services,
          schedule: {
            ...prev.services.schedule,
            open247: value
          }
        }
      }));
      return;
    }
    
    // For all other fields, they go under schedule
    setFormData(prev => {
      // Make a deep copy of the schedule object
      const newSchedule = JSON.parse(JSON.stringify(prev.services.schedule || {}));
      
      // Split the path and navigate to the right location
      const keys = fieldPath.split('.');
      let current = newSchedule;
      
      // Navigate to the parent object
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) {
          current[key] = {};
        }
        current = current[key];
      }
      
      // Set the value on the last key
      current[keys[keys.length - 1]] = value;
      
      return {
        ...prev,
        services: {
          ...prev.services,
          schedule: newSchedule
        }
      };
    });
  };


  // Handler for territories
  const handleTerritoriesChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      territories: {
        ...prev.territories,
        [name]: value,
      },
    }));
  };

  // Handler for ownership checkboxes
  const handleOwnershipChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      ownership: {
        ...prev.ownership,
        [name]: checked,
      },
    }));
  };

  // Handler for top-level checkboxes
  const handleTopLevelCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handler for nested fields (businessInfo)
  const handleBusinessInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        [name]: value,
      },
    }));
  };

  // Handler for insurance fields
  const handleInsuranceChange = (e) => {
    const { name, value } = e.target;
    // Phone/fax fields accept any format and normalize to (XXX) XXX-XXXX
    const nextValue = (name === 'agentPhone' || name === 'agentFax') ? formatPhoneNumber(value) : value;
    setFormData((prev) => ({
      ...prev,
      insurance: {
        ...prev.insurance,
        [name]: nextValue,
      },
    }));
  };

  // Handler for file inputs
  const handleFileChange = (e) => {
    const { name } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: e.target.files[0],
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      {submitStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Application submitted!</strong> Thank you — our provider relations team
          reviews applications within 3–5 business days. A confirmation email is on its
          way to the address you provided — <strong>if you don't see it, check your
          spam/junk folder</strong>. Questions? {supportEmail} or {supportPhone}.
        </Alert>
      )}
      {submitStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}
      {linkedVendorId && (
        <Alert severity="info" sx={{ mb: 1 }}>
          You are applying as an existing {orgName} partner. Provider ID <strong>{linkedVendorId}</strong> will
          be attached to your application, and your work history carries over once approved.
        </Alert>
      )}
      {/* Contact Information */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Contact Information</Typography>
        <TextField
          fullWidth
          required
          label="Company Name"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="MC Number"
          name="mcNumber"
          value={formData.mcNumber}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="US DOT Number"
          name="dotNumber"
          value={formData.dotNumber}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          required
          label="Owner's First Name"
          name="ownerFirstName"
          value={formData.ownerFirstName}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          required
          label="Owner's Last Name"
          name="ownerLastName"
          value={formData.ownerLastName}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          required
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          margin="dense"
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <InputLabel id="phone-country-label">Country</InputLabel>
            <Select
              fullWidth
              labelId="phone-country-label"
              name="phoneCountry"
              value={formData.phoneCountry}
              onChange={handleChange}
            >
              {countries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Code"
              name="phoneCountryCode"
              value={formData.phoneCountryCode}
              slotProps={{
                input: {
                  readOnly: true,
                }
              }}
              margin="dense"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Phone Number"
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
  {/* Existing fields */}
  
  {/* Add this new Grid item for files */}
  <Grid item xs={12}>
  <Box sx={{ mt: 2 }}>
    <Typography variant="h6">Uploaded Documents</Typography>

    {/* Each entry is either a just-picked File (public form — show its name)
        or a stored server path string (review contexts — show a view link) */}
    {[
      { label: 'W9 Form', value: formData.w9 },
      { label: 'Background Check', value: formData.backgroundCheck },
      { label: 'Certificate of Insurance', value: formData.insurance?.coi },
      { label: 'Zip Codes File', value: formData.territories?.zipCodeFile }
    ].filter((doc) => doc.value).map((doc) => (
      <Box display="flex" alignItems="center" mb={1} key={doc.label}>
        <Typography variant="subtitle2" sx={{ mr: 2 }}>{doc.label}:</Typography>
        {typeof doc.value === 'string' ? (
          <Link href={getDocumentUrl(doc.value)} target="_blank">
            View Document
          </Link>
        ) : (
          <Typography variant="body2">
            📎 {doc.value.name || 'File attached'} — ready to submit
          </Typography>
        )}
      </Box>
    ))}
  </Box>
  </Grid>
</Grid>
      </Box>

      {/* Facility Address */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Facility Address</Typography>
        <InputLabel id="facility-country-label">Facility Country</InputLabel>
        <Select
          fullWidth
          labelId="facility-country-label"
          name="facilityCountry"
          value={formData.facilityCountry}
          onChange={handleChange}
          margin="dense"
        >
          {countries.map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Select>
        <TextField
          fullWidth
          label="Facility Address Line 1"
          name="facilityAddress1"
          value={formData.facilityAddress1}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Facility Address Line 2"
          name="facilityAddress2"
          value={formData.facilityAddress2}
          onChange={handleChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="City"
          name="facilityCity"
          value={formData.facilityCity}
          onChange={handleChange}
          margin="dense"
        />
        <InputLabel id="facility-state-label">State/Province</InputLabel>
        <Select
          fullWidth
          labelId="facility-state-label"
          name="facilityState"
          value={formData.facilityState}
          onChange={handleChange}
          margin="dense"
        >
          {statesProvinces[formData.facilityCountry]?.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </Select>
        <TextField
          fullWidth
          label="Zip/Postal Code"
          name="facilityZip"
          value={formData.facilityZip}
          onChange={handleChange}
          margin="dense"
        />
      </Box>

      {/* Billing Information */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Billing Information</Typography>
        <InputLabel id="billing-same-label">
          Is your billing address the same as your facility address?
        </InputLabel>
        <Select
          fullWidth
          labelId="billing-same-label"
          name="billingSame"
          value={formData.billingSame}
          onChange={handleChange}
          margin="dense"
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        {/* Show billing address fields if billingSame is 'no' */}
        {formData.billingSame === 'no' && (
          <Box sx={{ mt: 2 }}>
            <InputLabel id="billing-country-label">Billing Country</InputLabel>
            <Select
              fullWidth
              labelId="billing-country-label"
              name="billingAddress.country"
              value={formData.billingAddress.country}
              onChange={handleChange}
              margin="dense"
            >
              {countries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth
              label="Billing Address Line 1"
              name="billingAddress.address1"
              value={formData.billingAddress.address1}
              onChange={handleChange}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Billing Address Line 2"
              name="billingAddress.address2"
              value={formData.billingAddress.address2}
              onChange={handleChange}
              margin="dense"
            />
            <TextField
              fullWidth
              label="City"
              name="billingAddress.city"
              value={formData.billingAddress.city}
              onChange={handleChange}
              margin="dense"
            />
            <InputLabel id="billing-state-label">State/Province</InputLabel>
            <Select
              fullWidth
              labelId="billing-state-label"
              name="billingAddress.state"
              value={formData.billingAddress.state}
              onChange={handleChange}
              margin="dense"
            >
              {statesProvinces[formData.billingAddress.country]?.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth
              label="Zip/Postal Code"
              name="billingAddress.zip"
              value={formData.billingAddress.zip}
              onChange={handleChange}
              margin="dense"
            />
          </Box>
        )}
      </Box>

      {/* Services You Perform */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Services You Perform</Typography>
        
        {/* Roadside Services */}
        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
          Roadside Services
        </Typography>
        <Box sx={{ ml: 2 }}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Jump Starts</Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.jumpStartLight}
                  onChange={handleServicesChange}
                  name="jumpStartLight"
                />
              }
              label="Light Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.jumpStartMedium}
                  onChange={handleServicesChange}
                  name="jumpStartMedium"
                />
              }
              label="Medium Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.jumpStartHeavy}
                  onChange={handleServicesChange}
                  name="jumpStartHeavy"
                />
              }
              label="Heavy Duty"
            />
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Tire Changes</Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.tireChangeLight}
                  onChange={handleServicesChange}
                  name="tireChangeLight"
                />
              }
              label="Light Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.tireChangeMedium}
                  onChange={handleServicesChange}
                  name="tireChangeMedium"
                />
              }
              label="Medium Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.tireChangeHeavy}
                  onChange={handleServicesChange}
                  name="tireChangeHeavy"
                />
              }
              label="Heavy Duty"
            />
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Lock Outs</Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.lockOutLight}
                  onChange={handleServicesChange}
                  name="lockOutLight"
                />
              }
              label="Light Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.lockOutMedium}
                  onChange={handleServicesChange}
                  name="lockOutMedium"
                />
              }
              label="Medium Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.lockOutHeavy}
                  onChange={handleServicesChange}
                  name="lockOutHeavy"
                />
              }
              label="Heavy Duty"
            />
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Fuel Deliveries</Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.fuelDeliveryLight}
                  onChange={handleServicesChange}
                  name="fuelDeliveryLight"
                />
              }
              label="Light Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.fuelDeliveryMedium}
                  onChange={handleServicesChange}
                  name="fuelDeliveryMedium"
                />
              }
              label="Medium Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.fuelDeliveryHeavy}
                  onChange={handleServicesChange}
                  name="fuelDeliveryHeavy"
                />
              }
              label="Heavy Duty"
            />
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Winch Outs</Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.winchOutLight}
                  onChange={handleServicesChange}
                  name="winchOutLight"
                />
              }
              label="Light Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.winchOutMedium}
                  onChange={handleServicesChange}
                  name="winchOutMedium"
                />
              }
              label="Medium Duty"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.winchOutHeavy}
                  onChange={handleServicesChange}
                  name="winchOutHeavy"
                />
              }
              label="Heavy Duty"
            />
          </FormGroup>
        </Box>

        {/* Towing Services */}
        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
          Towing Services
        </Typography>
        <Box sx={{ ml: 2 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.lightDutyTowing}
                  onChange={handleServicesChange}
                  name="lightDutyTowing"
                />
              }
              label="Light Duty Towing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.mediumDutyTowing}
                  onChange={handleServicesChange}
                  name="mediumDutyTowing"
                />
              }
              label="Medium Duty Towing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.heavyDutyTowing}
                  onChange={handleServicesChange}
                  name="heavyDutyTowing"
                />
              }
              label="Heavy Duty Towing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.accidentSceneTowing}
                  onChange={handleServicesChange}
                  name="accidentSceneTowing"
                />
              }
              label="Accident Scene Towing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.secondaryTow}
                  onChange={handleServicesChange}
                  name="secondaryTow"
                />
              }
              label="Secondary Tow"
            />
          </FormGroup>
        </Box>

        {/* Other Services */}
        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
          Other Services
        </Typography>
        <Box sx={{ ml: 2 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.mobileMechanic}
                  onChange={handleServicesChange}
                  name="mobileMechanic"
                />
              }
              label="Mobile Mechanic"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.mediumHeavyTire}
                  onChange={handleServicesChange}
                  name="mediumHeavyTire"
                />
              }
              label="Medium Heavy Tire"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.storageFacility}
                  onChange={handleServicesChange}
                  name="storageFacility"
                />
              }
              label="Storage Facility"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.keyMaking}
                  onChange={handleServicesChange}
                  name="keyMaking"
                />
              }
              label="Key Making"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.locksmith}
                  onChange={handleServicesChange}
                  name="locksmith"
                />
              }
              label="Locksmith"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.services.electricCharging}
                  onChange={handleServicesChange}
                  name="electricCharging"
                />
              }
              label="Electric Charging"
            />
          </FormGroup>
        </Box>
        {/* Hours of Operation */}
        <Box sx={{ mt: 3 }}>
           <Typography variant="h6">Hours of Operation</Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
             Check "Open 24/7" if you operate around the clock, or set your available hours for each day.
           </Typography>
           <ScheduleInput
             scheduleData={formData.services?.schedule || {}}
             onChange={handleServiceScheduleChange}
             // disabled={false} // Not disabled on public form
           />
        </Box>
      </Box>

      {/* Territories */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Territories</Typography>
        <Button variant="contained" component="label" sx={{ mt: 1 }}>
          Upload Zip Code List
          <input
            type="file"
            hidden
            name="zipCodeFile"
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                territories: {
                  ...prev.territories,
                  zipCodeFile: e.target.files[0],
                },
              }))
            }
          />
        </Button>
        {formData.territories.zipCodeFile && (
          <Typography>{formData.territories.zipCodeFile.name}</Typography>
        )}
        <Typography variant="body2" sx={{ mt: 1 }}>
          Or type in your zip code list, separating each with a comma and space here:
        </Typography>
        <TextField
          fullWidth
          multiline
          name="zipCodes"
          value={formData.territories.zipCodes}
          onChange={handleTerritoriesChange}
          inputProps={{ maxLength: 500 }}
          margin="dense"
        />
        <FormHelperText>{`${formData.territories.zipCodes.length}/500 characters`}</FormHelperText>
      </Box>

      {/* IRS Information */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">IRS Information</Typography>
        <Button variant="contained" component="label" sx={{ mt: 1 }}>
          Upload W-9 Form
          <input type="file" hidden name="w9" onChange={handleFileChange} />
        </Button>
        {formData.w9 && <Typography>{formData.w9.name}</Typography>}
        <InputLabel id="w9-address-label" sx={{ mt: 2 }}>
          Address on W-9 Form
        </InputLabel>
        <Select
          fullWidth
          labelId="w9-address-label"
          name="w9Address"
          value={formData.w9Address}
          onChange={handleChange}
          margin="dense"
        >
          <MenuItem value="facility">Facility Address</MenuItem>
          <MenuItem value="billing">Billing Address</MenuItem>
        </Select>
        <FormControlLabel
          control={
            <Checkbox
              name="backupWithholding"
              checked={formData.backupWithholding}
              onChange={handleTopLevelCheckboxChange}
            />
          }
          label="Backup Withholding Certification"
        />
      </Box>

      {/* Electronic Signature */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Electronic Signature</Typography>
        <TextField
          fullWidth
          label="Electronic Signature"
          name="signature"
          value={formData.signature}
          onChange={handleChange}
          margin="dense"
        />
      </Box>

      {/* Number of Employees */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Number of Employees</Typography>
        <TextField
          fullWidth
          label="Number of W2 Employees (including yourself — do not count 1099 contractors)"
          type="number"
          name="employees"
          value={formData.employees}
          onChange={handleChange}
          margin="dense"
        />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
          Businesses with W2 employees are required to carry Workers' Compensation insurance.
        </Typography>
      </Box>

      {/* Background Check */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Background Check Documentation</Typography>
        <Typography variant="body2">
          Background checks are required for all members of your business. If you do
          not provide background checks, your application will not be considered.
        </Typography>
        <Button variant="contained" component="label" sx={{ mt: 1 }}>
          Upload Background Check
          <input type="file" hidden name="backgroundCheck" onChange={handleFileChange} />
        </Button>
        {formData.backgroundCheck && (
          <Typography>{formData.backgroundCheck.name}</Typography>
        )}
      </Box>

      {/* Insurance Information */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Insurance Information</Typography>
        <Typography variant="body2">
          The following insurance coverage is required to partner with us:
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 1, pl: 3 }}>
          <Typography component="li" variant="body2">
            Commercial General Liability — minimum $1,000,000 coverage
          </Typography>
          <Typography component="li" variant="body2">
            Commercial Auto Liability — minimum $1,000,000 coverage
          </Typography>
          <Typography component="li" variant="body2">
            On-Hook Coverage — minimum $2,000,000 coverage (required if you provide any towing services)
          </Typography>
          <Typography component="li" variant="body2">
            Workers' Compensation — required if you have any W2 employees
          </Typography>
          <Typography component="li" variant="body2">
            Garagekeepers Legal Liability — required if you provide storage services
          </Typography>
        </Box>
        <Typography variant="body2">
          Please upload your Certificate of Insurance. COIs that do not meet these
          minimums may delay your application or cause it to be rejected.
        </Typography>
        <Button variant="contained" component="label" sx={{ mt: 1 }}>
          Upload Certificate of Insurance
          <input
            type="file"
            hidden
            name="coi"
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                insurance: {
                  ...prev.insurance,
                  coi: e.target.files[0],
                },
              }))
            }
          />
        </Button>
        {formData.insurance.coi && <Typography>{formData.insurance.coi.name}</Typography>}
        <TextField
          fullWidth
          label="Insurance Agency"
          name="agency"
          value={formData.insurance.agency}
          onChange={handleInsuranceChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Policy #"
          name="policyNumber"
          value={formData.insurance.policyNumber}
          onChange={handleInsuranceChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Agent's Name"
          name="agentName"
          value={formData.insurance.agentName}
          onChange={handleInsuranceChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Agent's Phone"
          name="agentPhone"
          type="tel"
          value={formData.insurance.agentPhone}
          onChange={handleInsuranceChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Agent's Fax"
          name="agentFax"
          type="tel"
          value={formData.insurance.agentFax}
          onChange={handleInsuranceChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Agent's Email"
          name="agentEmail"
          type="email"
          value={formData.insurance.agentEmail}
          onChange={handleInsuranceChange}
          margin="dense"
        />
        <TextField
          fullWidth
          label="Policy Expiration Date"
          name="policyExpiration"
          type="date"
          value={formData.insurance.policyExpiration}
          onChange={handleInsuranceChange}
          margin="dense"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Box>

      {/* Additional Company Info */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Additional Company Info</Typography>
        <InputLabel id="years-in-business-label">
          How long has your company been in business?
        </InputLabel>
        <Select
          fullWidth
          labelId="years-in-business-label"
          name="yearsInBusiness"
          value={formData.businessInfo.yearsInBusiness}
          onChange={handleBusinessInfoChange}
          margin="dense"
        >
          {yearsOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <InputLabel id="ev-experience-label">
          Do you have experience handling Electric Vehicles?
        </InputLabel>
        <Select
          fullWidth
          labelId="ev-experience-label"
          name="electricVehicleExp"
          value={formData.businessInfo.electricVehicleExp}
          onChange={handleBusinessInfoChange}
          margin="dense"
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <InputLabel id="digital-dispatch-label">
          Do you currently use digital dispatch software?
        </InputLabel>
        <Select
          fullWidth
          labelId="digital-dispatch-label"
          name="digitalDispatch"
          value={formData.businessInfo.digitalDispatch}
          onChange={handleBusinessInfoChange}
          margin="dense"
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        {/* Conditionally render Dispatch Software dropdown */}
        {formData.businessInfo.digitalDispatch === 'yes' && (
          <>
            <InputLabel id="dispatch-software-label" sx={{ mt: 2 }}>
              Which dispatch software do you use?
            </InputLabel>
            <Select
              fullWidth
              labelId="dispatch-software-label"
              name="dispatchSoftware" // Ensure name matches state key
              value={formData.businessInfo.dispatchSoftware}
              onChange={handleBusinessInfoChange} // Reuse existing handler
              margin="dense"
            >
              {dispatchSoftwareOptions.map((software) => (
                <MenuItem key={software} value={software}>
                  {software}
                </MenuItem>
              ))}
            </Select>
          </>
        )}

        <InputLabel id="lead-source-label" sx={{ mt: 2 }}>
          How did you hear about us?
        </InputLabel>
        <Select
          fullWidth
          labelId="lead-source-label"
          name="leadSource"
          value={formData.leadSource}
          onChange={handleChange}
          margin="dense"
        >
          {['Referral from another provider', 'Email', 'Phone call', 'Facebook', 'Google search', 'Trade association', 'Other'].map((source) => (
            <MenuItem key={source} value={source}>
              {source}
            </MenuItem>
          ))}
        </Select>

        <TextField
          fullWidth
          label="Referral code (optional — the vendor number of the company that referred you)"
          name="referralCode"
          value={formData.referralCode}
          onChange={handleChange}
          margin="dense"
        />

        {/* Add Other Motorclub Services Question */}
        <InputLabel id="other-motorclub-label" sx={{ mt: 2 }}>
          Do you provide services for any other motorclubs?
        </InputLabel>
        <Select
          fullWidth
          labelId="other-motorclub-label"
          name="otherMotorclubServices"
          value={formData.businessInfo.otherMotorclubServices}
          onChange={handleBusinessInfoChange}
          margin="dense"
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        {/* Conditionally render Motorclub List field */}
        {formData.businessInfo.otherMotorclubServices === 'yes' && (
          <>
            <InputLabel id="other-motorclubs-list-label" sx={{ mt: 2 }}>
              Please list the other motorclubs you service (separate with commas):
            </InputLabel>
            <TextField
              fullWidth
              labelId="other-motorclubs-list-label"
              name="otherMotorclubsList"
              value={formData.businessInfo.otherMotorclubsList}
              onChange={handleBusinessInfoChange}
              margin="dense"
              multiline
              rows={3}
            />
          </>
        )}
      </Box>

      {/* Business Ownership */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Business Ownership Identification</Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.familyOwned}
                onChange={handleOwnershipChange}
                name="familyOwned"
              />
            }
            label="Family-Owned"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.womenOwned}
                onChange={handleOwnershipChange}
                name="womenOwned"
              />
            }
            label="Women-Owned"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.minorityOwned}
                onChange={handleOwnershipChange}
                name="minorityOwned"
              />
            }
            label="Minority-Owned"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.veteranOwned}
                onChange={handleOwnershipChange}
                name="veteranOwned"
              />
            }
            label="Veteran-Owned"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.lgbtqOwned}
                onChange={handleOwnershipChange}
                name="lgbtqOwned"
              />
            }
            label="LGBTQ+-Owned"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.smallBusiness}
                onChange={handleOwnershipChange}
                name="smallBusiness"
              />
            }
            label="Small Business"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ownership.disadvantagedBusiness}
                onChange={handleOwnershipChange}
                name="disadvantagedBusiness"
              />
            }
            label="Small Disadvantaged Business"
          />
        </FormGroup>
      </Box>

      {/* Terms and Conditions */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Terms and Conditions</Typography>
        <FormControlLabel
          control={
            <Checkbox
              name="termsAgreement"
              checked={formData.termsAgreement}
              onChange={handleTopLevelCheckboxChange}
              required
            />
          }
          label={
            <>
              By checking this box, I agree to{' '}
              <Link href={agreementUrl} target="_blank" rel="noopener">
                {isPlatform ? `${orgName}'s Terms and Conditions.*` : `${orgName}'s Service Provider Agreement.*`}
              </Link>
            </>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              name="codeOfConductAgreement"
              checked={formData.codeOfConductAgreement}
              onChange={handleTopLevelCheckboxChange}
              required
            />
          }
          label={
            <>
              By checking this box, I agree to{' '}
              <Link href={conductUrl} target="_blank" rel="noopener">
                {orgName}'s Code of Conduct.*
              </Link>
            </>
          }
        />
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{ mt: 2, mb: 4 }}
        disabled={!formData.termsAgreement || !formData.codeOfConductAgreement ||
                  submitStatus === 'submitting' || submitStatus === 'success'}
      >
        {submitStatus === 'submitting' ? 'Submitting...' :
         submitStatus === 'success' ? 'Application Submitted ✓' : 'Submit Application'}
      </Button>
    </form>
  );
};

export default ApplicationForm;
